import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';

function invalidateUpcoming(qc) {
  qc.invalidateQueries({ queryKey: ['upcoming-events'] });
  qc.invalidateQueries({ queryKey: ['cash-flow'] });
  // Inconditionnel (pas seulement pour source === 'client_invoice') : invalider une
  // clé absente du cache est un no-op peu coûteux, et ça évite un état périmé si
  // /factures a déjà été visitée dans la même session (staleTime 30s) — sans ça,
  // une facture marquée payée depuis "À payer" restait affichée "Ouverte" sur
  // /factures tant que le cache n'expirait pas.
  qc.invalidateQueries({ queryKey: ['client-invoices'] });
}

export function useMarkAsPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (event) => {
      if (event.source === 'future_expense') {
        return api.patch(`/future-expenses/${event.source_id}`, { status: 'realise' });
      }
      if (event.source === 'recurrence') {
        return api.post('/recurrence-payment-marks', {
          recurrence_id: event.source_id,
          period_date: event.date,
        });
      }
      if (event.source === 'debt_schedule') {
        return api.post('/debt-payment-marks', {
          debt_id: event.source_id,
          period_date: event.date,
        });
      }
      if (event.source === 'client_invoice') {
        // Si la réconciliation auto a déjà trouvé une transaction Qonto correspondante
        // (event.matched_transaction_id), on la lie à la facture pour l'audit trail.
        // Sinon on marque simplement payé (paid_at par défaut = aujourd'hui côté backend).
        const body = event.matched_transaction_id
          ? { transaction_id: event.matched_transaction_id }
          : {};
        return api.post(`/client-invoices/${event.source_id}/mark-paid`, body);
      }
      throw new Error(`mark-as-paid non supporté pour source=${event.source}`);
    },
    onSuccess: () => invalidateUpcoming(qc),
  });
}

export function useUnmarkAsPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (event) => {
      // client_invoice volontairement exclu : le démarquage d'une facture se fait
      // uniquement depuis /factures (unmark-paid), jamais depuis "À payer".
      if (event.source !== 'recurrence' && event.source !== 'debt_schedule') {
        throw new Error('unmark uniquement supporté pour recurrence et debt_schedule manual_mark');
      }
      const path = event.source === 'recurrence' ? '/recurrence-payment-marks' : '/debt-payment-marks';
      const idField = event.source === 'recurrence' ? 'recurrence_id' : 'debt_id';
      return api.deleteWithBody(path, {
        [idField]: event.source_id,
        period_date: event.mark_period_date ?? event.date,
      });
    },
    onSuccess: () => invalidateUpcoming(qc),
  });
}
